import { useState } from 'react';
import { Plus, Pencil, Settings2, Archive, Users, Lock } from 'lucide-react';
import { useEntitiesAdmin, useDeleteEntity } from '../hooks/useAdminEntities.js';
import { useEntityMembers } from '../hooks/useEntityMembers.js';
import EntityFormModal from '../components/EntityFormModal.jsx';
import IntegrationsModal from '../components/IntegrationsModal.jsx';
import EntitySharePanel from '../components/EntitySharePanel.jsx';

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const styles = {
    active: 'bg-green-100 text-green-700',
    suspended: 'bg-orange-100 text-orange-700',
    archived: 'bg-slate-100 text-slate-500',
  };
  const labels = {
    active: 'Actif',
    suspended: 'Suspendu',
    archived: 'Archivé',
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] ?? styles.archived}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

// ── Privée / Partagée badge ──────────────────────────────────────────────────
// Historical entities (created before Task 2 — private entities) have no
// owner_user_id: they're treated as shared regardless of member count.

function ShareBadge({ entity, memberCount, isLoading }) {
  if (entity.owner_user_id == null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
        Partagée
      </span>
    );
  }
  if (isLoading) {
    return <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-400">…</span>;
  }
  const isShared = (memberCount ?? 1) > 1;
  return isShared ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
      Partagée
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
      <Lock size={10} aria-hidden />
      Privée
    </span>
  );
}

// ── Entity card ───────────────────────────────────────────────────────────────

function EntityCard({ entity, onEdit, onIntegrations, onArchive }) {
  const [shareOpen, setShareOpen] = useState(false);
  const { data: members, isLoading: membersLoading } = useEntityMembers(entity.id);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{entity.name}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-500">
              {entity.slug}
            </span>
            <StatusBadge status={entity.status} />
            <ShareBadge entity={entity} memberCount={members?.length} isLoading={membersLoading} />
          </div>
          {entity.legal_name && (
            <p className="mt-0.5 text-xs text-slate-500">{entity.legal_name}</p>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
        {entity.country && (
          <span>
            Pays&nbsp;: <strong className="text-slate-700">{entity.country}</strong>
          </span>
        )}
        {entity.currency && (
          <span>
            Devise&nbsp;: <strong className="text-slate-700">{entity.currency}</strong>
          </span>
        )}
        {entity.siret && (
          <span>
            SIRET&nbsp;: <strong className="text-slate-700">{entity.siret}</strong>
          </span>
        )}
        {entity.vat_number && (
          <span>
            TVA&nbsp;: <strong className="text-slate-700">{entity.vat_number}</strong>
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onEdit(entity)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          aria-label={`Modifier l'entité ${entity.name}`}
        >
          <Pencil size={12} />
          Modifier
        </button>
        <button
          type="button"
          onClick={() => onIntegrations(entity)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          aria-label={`Configurer les intégrations de ${entity.name}`}
        >
          <Settings2 size={12} />
          Intégrations
        </button>
        {entity.status !== 'archived' && (
          <button
            type="button"
            onClick={() => onArchive(entity)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50"
            aria-label={`Archiver l'entité ${entity.name}`}
          >
            <Archive size={12} />
            Archiver
          </button>
        )}
        <button
          type="button"
          onClick={() => setShareOpen((v) => !v)}
          aria-expanded={shareOpen}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          aria-label={`Gérer le partage de ${entity.name}`}
        >
          <Users size={12} />
          Partage
        </button>
      </div>

      {shareOpen && <EntitySharePanel entityId={entity.id} entityName={entity.name} />}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function EntitiesAdminPage() {
  const { data: entities, isLoading, error } = useEntitiesAdmin();
  const deleteEntity = useDeleteEntity();

  const [entityModal, setEntityModal] = useState({ open: false, entity: null });
  const [integrationsModal, setIntegrationsModal] = useState({ open: false, entity: null });

  function openCreate() {
    setEntityModal({ open: true, entity: null });
  }

  function openEdit(entity) {
    setEntityModal({ open: true, entity });
  }

  function openIntegrations(entity) {
    setIntegrationsModal({ open: true, entity });
  }

  async function handleArchive(entity) {
    if (
      !window.confirm(
        `Archiver l'entité "${entity.name}" ?\nCette action est réversible (vous pourrez la réactiver via Modifier).`
      )
    )
      return;
    await deleteEntity.mutateAsync(entity.id);
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 md:px-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 md:text-2xl">Gestion des entités</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Gérez les entités multi-tenant et leurs intégrations bancaires / e-commerce.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Nouvelle entité</span>
          <span className="sm:hidden">Nouveau</span>
        </button>
      </div>

      {/* States */}
      {isLoading && (
        <p className="py-12 text-center text-sm text-slate-500">Chargement...</p>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          Impossible de charger les entités&nbsp;: {error.message}
        </div>
      )}

      {deleteEntity.error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          Erreur lors de l'archivage&nbsp;: {deleteEntity.error.message}
        </div>
      )}

      {/* Entity list */}
      {!isLoading && !error && (
        <>
          {entities?.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 px-6 py-12 text-center">
              <p className="text-sm text-slate-500">Aucune entité pour l'instant.</p>
              <button
                type="button"
                onClick={openCreate}
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-700 underline hover:text-slate-900"
              >
                <Plus size={14} />
                Créer la première entité
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {entities.map((entity) => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  onEdit={openEdit}
                  onIntegrations={openIntegrations}
                  onArchive={handleArchive}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <EntityFormModal
        open={entityModal.open}
        onClose={() => setEntityModal({ open: false, entity: null })}
        entity={entityModal.entity}
      />

      <IntegrationsModal
        open={integrationsModal.open}
        onClose={() => setIntegrationsModal({ open: false, entity: null })}
        entity={integrationsModal.entity}
      />
    </div>
  );
}
